import React from "react";
import { sGetRaw, sSetRaw } from "../lib/scopedStorage";
import type { Theme } from "../lib/theme";
import OEWordmark from "../components/OEWordmark";
import * as Icon from "../components/OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface HomeTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  cloudLoading: boolean;
  isDarkMode: boolean;
  setIsDarkMode: (v: boolean) => void;
  user: any;
  signOut?: () => void;
  showAccountMenu: boolean;
  setShowAccountMenu: React.Dispatch<React.SetStateAction<boolean>>;
  onBackToProjects?: () => void;
  setShowNewProjDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenSandbox: () => void;
  setActiveTab: (tab: string) => void;
  setProjectName: (v: string) => void;
  setBuildingArea: (v: number) => void;
  setDuration: (v: number) => void;
  setOverhead: (v: number) => void;
  setProfit: (v: number) => void;
  setContingency: (v: number) => void;
  setMarkupMode: React.Dispatch<React.SetStateAction<any>>;
  setProposals: (v: any[]) => void;
  setProposalAreas: (v: any) => void;
  setDivisionSettings: (v: any[]) => void;
  setSubSettings: (v: any) => void;
  setDivisionsRaw: (v: any[]) => void;
  setActiveDiv: (v: string) => void;
  setItemStateRaw: (v: any) => void;
  setProjectSnapshots: (v: any[]) => void;
  aiFileRef: React.RefObject<HTMLInputElement>;
  getFileSummary: (f: any) => {total: number; costPerSF: number};
  fmt: (n: number) => string;
  fmtSF: (n: number) => string;
  setEditDisplayName: React.Dispatch<React.SetStateAction<string>>;
  DEFAULT_PROPOSALS: any[];
}

export default function HomeTab({
  T, isMobile, isTablet,
  cloudLoading, isDarkMode, setIsDarkMode,
  user, signOut, showAccountMenu, setShowAccountMenu,
  onBackToProjects, setShowNewProjDialog, handleOpenSandbox, setActiveTab,
  setProjectName, setBuildingArea, setDuration, setOverhead, setProfit,
  setContingency, setMarkupMode, setProposals, setProposalAreas,
  setDivisionSettings, setSubSettings, setDivisionsRaw, setActiveDiv,
  setItemStateRaw, setProjectSnapshots,
  aiFileRef, getFileSummary, fmt, fmtSF, setEditDisplayName, DEFAULT_PROPOSALS,
}: HomeTabProps) {
  const hPad = isMobile ? "16px" : isTablet ? "24px" : "32px";

  if (cloudLoading) {
    return (
      <div style={{flex:1,display:"flex",flexDirection:"column" as any,alignItems:"center",justifyContent:"center",gap:16,padding:40,minHeight:300}}>
        <div style={{width:36,height:36,borderRadius:"50%",border:`3px solid ${T.borderAccent}33`,borderTopColor:T.borderAccent,animation:"spin 0.8s linear infinite"}}/>
        <div style={{fontSize:14,color:T.textMuted,fontWeight:500}}>Loading project…</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const savedFiles = JSON.parse(sGetRaw("openestimate_files_v2") || "{}");

  const handleOpenFile = (name: string) => {
    const f = savedFiles[name];
    if (!f) return;
    setProjectName(name);
    if (f.buildingArea != null) setBuildingArea(f.buildingArea);
    if (f.duration != null) setDuration(f.duration);
    if (f.overhead != null) setOverhead(f.overhead);
    if (f.profit != null) setProfit(f.profit);
    if (f.contingency != null) setContingency(f.contingency);
    if (f.markupMode) setMarkupMode(f.markupMode);
    setProposals(f.proposals?.length ? f.proposals : DEFAULT_PROPOSALS);
    setProposalAreas(f.proposalAreas || {});
    setDivisionSettings(f.divisionSettings || []);
    setSubSettings(f.subSettings || {});
    setDivisionsRaw(f.divisions || []);
    setActiveDiv((f.divisions || [])[0]?.id || "div1");
    setItemStateRaw(f.itemState || {});
    if (f.projectSnapshots) setProjectSnapshots(f.projectSnapshots);
    setActiveTab("estimate");
  };

  const handleDeleteFile = (name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    const copy = {...savedFiles};
    delete copy[name];
    sSetRaw("openestimate_files_v2", JSON.stringify(copy));
    setActiveTab("home");
  };

  return (
    <div className="fade-in" style={{display:"flex",flexDirection:"column"as any,minHeight:0}}>
      {/* ── Home hero header — matches app header ── */}
      <div style={{background:T.bgHeader,borderBottom:`1px solid ${T.borderPrimary}`,padding:`13px ${hPad}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <OEWordmark
          size={22}
          wordColor={T.textInverse}
          accentColor={T.borderAccent}
          subColor={T.textMuted}
          circleColor="white"
          onClick={onBackToProjects ?? undefined}
          title={onBackToProjects ? "Back to Projects" : undefined}
        />
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>{const next=!isDarkMode;setIsDarkMode(next);try{sSetRaw("oe_dark_mode",String(next));}catch{};}}
            style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,cursor:"pointer",padding:"4px 10px",borderRadius:3,fontSize:10,fontFamily:FONT,letterSpacing:".06em"}}>
            {isDarkMode?<><Icon.Sun size={11}/> Light</>:<><Icon.Moon size={11}/> Dark</>}
          </button>
          {user&&(<div style={{position:"relative"}}>
            <button className="account-avatar" onClick={e=>{e.stopPropagation();setShowAccountMenu(v=>!v);}} title={user.displayName}>
              {user.avatarUrl?<img src={user.avatarUrl} alt="" style={{width:28,height:28,borderRadius:"50%"}}/>
                :(user.displayName||user.email||"?").split(" ").map((w:string)=>w[0]).join("").toUpperCase().slice(0,2)}
            </button>
            {showAccountMenu&&(<div className="account-menu" onClick={e=>e.stopPropagation()}>
              <div className="account-menu-header">
                <div style={{fontSize:13,fontWeight:700,color:T.textSecondary}}>{user.displayName}</div>
                <div style={{fontSize:11,color:T.textWeak,marginTop:2}}>{user.email}</div>
              </div>
              <div className="account-menu-divider"/>
              <div className="account-menu-item" onClick={()=>{setActiveTab("settings-profile");setShowAccountMenu(false);setEditDisplayName(user.displayName||"");}}>
                <span style={{width:16,textAlign:"center",fontSize:13}}>&#128100;</span> Profile
              </div>
              <div className="account-menu-item" onClick={()=>{setActiveTab("settings-team");setShowAccountMenu(false);}}>
                <span style={{width:16,textAlign:"center",fontSize:13}}>&#127970;</span> Organizations
              </div>
              <div className="account-menu-divider"/>
              <div className="account-menu-item" onClick={()=>{setShowAccountMenu(false);window.location.href="mailto:feedback@open-estimate.com?subject=OpenEstimate%20Feedback";}}>
                <span style={{width:16,textAlign:"center",fontSize:13}}>&#9993;</span> Send Feedback
              </div>
              <div className="account-menu-divider"/>
              <div className="account-menu-item" style={{color:T.negative}} onClick={()=>{setShowAccountMenu(false);signOut&&signOut();}}>
                <span style={{width:16,textAlign:"center",fontSize:13}}>&#8617;</span> Sign Out
              </div>
            </div>)}
          </div>)}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:1280,overflowY:"auto"as any,flex:1}}>
        <div style={{display:"flex",gap:10,marginBottom:28,flexWrap:"wrap"}}>
          <button className="home-action-btn primary" onClick={()=>setShowNewProjDialog(true)}>+ New Project</button>
          <button className="home-action-btn" onClick={handleOpenSandbox} title="Load a fully-built demo estimate">&#129514; Sandbox</button>
          {user&&<button className="home-action-btn" onClick={()=>setActiveTab("settings-team")}>&#127970; Organizations</button>}
          <button className="home-action-btn" onClick={()=>setActiveTab("settings-presets")} title="Presets">&#9881; Presets</button>
        </div>

        <div style={{fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",marginBottom:14}}>
          Recent Projects
          <span style={{fontWeight:400,opacity:.55,marginLeft:6}}>({Object.keys(savedFiles).length})</span>
        </div>
        {Object.keys(savedFiles).length===0?(
          <div style={{textAlign:"center",padding:"60px 32px",border:`1px dashed ${T.borderSecondary}`,borderRadius:6}}>
            <div style={{fontSize:32,marginBottom:14,opacity:.25}}><Icon.Ruler size={36}/></div>
            <div style={{fontSize:16,fontWeight:700,color:T.textSubLabel,marginBottom:8}}>No projects yet</div>
            <div style={{fontSize:12,color:T.textMuted,lineHeight:1.7,marginBottom:24,maxWidth:320,marginLeft:"auto",marginRight:"auto"}}>
              Create your first estimate, import an existing one with AI, or explore the sandbox to see what's possible.
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap" as any}}>
              <button className="home-action-btn primary" onClick={()=>setShowNewProjDialog(true)}>+ New Project</button>
              <button className="home-action-btn" onClick={()=>{aiFileRef?.current?.click?.()}} title="Import existing estimate"><Icon.Bot size={13}/> Import with AI</button>
              <button className="home-action-btn" onClick={handleOpenSandbox}><Icon.FlaskConical size={13}/> Try Sandbox</button>
            </div>
          </div>
        ):(
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":isTablet?"repeat(2,1fr)":"repeat(3,1fr)",gap:14}}>
            {Object.entries(savedFiles as Record<string,any>)
              .sort(([,a],[,b])=>new Date(b.savedAt).getTime()-new Date(a.savedAt).getTime())
              .map(([name,f])=>{
                const {total,costPerSF}=getFileSummary(f);
                const snaps:any[]=(f.projectSnapshots||[]);
                const CARD_BKGS=["#0e2238","#0e2a16","#2a2208","#2a0e0e","#0e1632","#1e0e2a"];
                const CARD_ICS=["#4a8fbf","#4abf6a","#bfb04a","#bf6a4a","#6a7abf","#8a6abf"];
                let h=0;for(let ci=0;ci<name.length;ci++)h=name.charCodeAt(ci)+((h<<5)-h);
                const ci=Math.abs(h)%6;
                const cardBg=CARD_BKGS[ci],cardIc=CARD_ICS[ci];
                const divCount=(f.divisionSettings||[]).filter((d:any)=>d.enabled!==false).length;
                const savedAgo=(()=>{const s=Math.floor((Date.now()-new Date(f.savedAt).getTime())/1000);if(s<60)return"just now";if(s<3600)return`${Math.floor(s/60)}m ago`;if(s<86400)return`${Math.floor(s/3600)}h ago`;return`${Math.floor(s/86400)}d ago`;})();
                return (
                  <div key={name}
                    style={{background:T.bgPanel,border:`1px solid ${T.borderSecondary}`,borderRadius:6,overflow:"hidden",
                      display:"flex",cursor:"pointer",transition:"border-color .15s,box-shadow .15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderHover;e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.18)";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.borderSecondary;e.currentTarget.style.boxShadow="none";}}
                    onClick={()=>handleOpenFile(name)}>
                    <div style={{width:90,flexShrink:0,background:cardBg,display:"flex",flexDirection:"column" as any,
                      alignItems:"center",justifyContent:"center",padding:"14px 6px",gap:6}}>
                      <svg viewBox="0 0 24 24" width={32} height={32} fill="none" stroke={cardIc} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.83 2.83 0 014 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                      </svg>
                      <div style={{fontSize:10,fontWeight:700,color:cardIc,opacity:.7,letterSpacing:".05em",textAlign:"center" as any}}>
                        {name.split(/\s+/).slice(0,2).map((w:string)=>w[0]).join("").toUpperCase()}
                      </div>
                    </div>
                    <div style={{flex:1,padding:"12px 14px",display:"flex",flexDirection:"column" as any,justifyContent:"space-between",minWidth:0}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:T.textSubLabel,lineHeight:1.3,marginBottom:2,
                          overflow:"hidden",display:"-webkit-box" as any,WebkitLineClamp:2,WebkitBoxOrient:"vertical" as any}}>
                          {name}
                        </div>
                        {f.address&&<div style={{fontSize:10,color:T.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginBottom:2}}>{f.address}</div>}
                      </div>
                      <div style={{margin:"6px 0"}}>
                        <div style={{fontSize:16,fontWeight:700,color:total>0?T.borderAccent:T.textWeak,lineHeight:1}}>
                          {total>0?fmt(total):"—"}
                        </div>
                        {costPerSF>0&&<div style={{fontSize:10,color:T.positive,marginTop:2}}>{fmtSF(costPerSF)}/SF</div>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:4}}>
                        <div style={{fontSize:10,color:T.textWeak,display:"flex",alignItems:"center",gap:6}}>
                          <span title={new Date(f.savedAt).toLocaleString()}><Icon.Clock size={11}/> {savedAgo}</span>
                          {snaps.length>0&&<span style={{background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:10,padding:"1px 6px"}}><Icon.Pin size={11}/> {snaps.length}</span>}
                          {divCount>0&&<span style={{background:T.bgInput,border:`1px solid ${T.borderSecondary}`,borderRadius:10,padding:"1px 6px"}}>{divCount} div</span>}
                        </div>
                        <button onClick={e=>{e.stopPropagation();handleDeleteFile(name);}}
                          style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:14,padding:"0 2px",lineHeight:1,opacity:.4,flexShrink:0}}
                          onMouseEnter={e=>{e.currentTarget.style.opacity="1";e.currentTarget.style.color=T.negative;}}
                          onMouseLeave={e=>{e.currentTarget.style.opacity=".4";e.currentTarget.style.color=T.textWeak;}}>
                          <Icon.X size={10}/>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
